import { useState, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AuditLogTabs } from '@/components/audit-log-tabs';
import { useAnalytics } from '@/hooks/use-analytics';
import { AnalyticsSummary } from '@/components/analytics-summary';
import {
  TimeSeriesChart,
  TopEventTypesChart,
  ResponseTimesTable,
} from '@/components/analytics-charts';
import { ANALYTICS_TIME_RANGE_LABELS, type AnalyticsTimeRange } from '@/types/analytics';
import { getCategoriesForTab, type AuditLogTab } from '@/types/audit-log';

/**
 * Analytics Page
 *
 * Analytics dashboard with charts, metrics, and insights.
 * Provides high-level overview of audit log data with visualizations.
 * Supports tab-based filtering to show analytics for Application or Admin logs separately.
 */
export function AnalyticsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<AnalyticsTimeRange>('24h');

  // Get active tab from URL (default to 'application')
  const activeTab = (searchParams.get('tab') as AuditLogTab) || 'application';

  // Derive categories from active tab
  const categories = useMemo(() => getCategoriesForTab(activeTab), [activeTab]);

  const {
    data: analytics,
    isLoading,
    isError,
    error,
    refetch,
  } = useAnalytics(timeRange, categories);

  /**
   * Handle time range change
   */
  const handleTimeRangeChange = (value: string) => {
    setTimeRange(value as AnalyticsTimeRange);
  };

  /**
   * Handle retry
   */
  const handleRetry = () => {
    void refetch();
  };

  /**
   * Handle endpoint click - navigate to endpoint details page
   */
  const handleEndpointClick = (endpoint: string) => {
    navigate(`/analytics/endpoint/${encodeURIComponent(endpoint)}`);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Analytics Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Visualize trends, patterns, and insights from audit log data
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Time Range:</span>
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1h">{ANALYTICS_TIME_RANGE_LABELS['1h']}</SelectItem>
              <SelectItem value="24h">{ANALYTICS_TIME_RANGE_LABELS['24h']}</SelectItem>
              <SelectItem value="7d">{ANALYTICS_TIME_RANGE_LABELS['7d']}</SelectItem>
              <SelectItem value="30d">{ANALYTICS_TIME_RANGE_LABELS['30d']}</SelectItem>
              <SelectItem value="90d">{ANALYTICS_TIME_RANGE_LABELS['90d']}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRetry}
            disabled={isLoading}
            title="Refresh analytics"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <AuditLogTabs />
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading analytics data...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Analytics</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load analytics data'}
          </AlertDescription>
          <Button variant="outline" size="sm" onClick={handleRetry} className="mt-4">
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </Alert>
      )}

      {/* Success State - Show Analytics */}
      {!isLoading && !isError && analytics && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <AnalyticsSummary data={analytics} />

          {/* Time Series Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Events Over Time</CardTitle>
              <CardDescription>Time series visualization of audit log events</CardDescription>
            </CardHeader>
            <CardContent>
              <TimeSeriesChart data={analytics.timeSeries} />
            </CardContent>
          </Card>

          {/* Two Column Layout */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Top Event Types */}
            <Card>
              <CardHeader>
                <CardTitle>Top Event Types</CardTitle>
                <CardDescription>Most frequent event types by occurrence</CardDescription>
              </CardHeader>
              <CardContent>
                <TopEventTypesChart data={analytics.topEventTypes} />
              </CardContent>
            </Card>

            {/* Endpoint Performance */}
            <Card>
              <CardHeader>
                <CardTitle>Endpoint Performance</CardTitle>
                <CardDescription>
                  Average response times and error rates. Click a row to view requests.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponseTimesTable
                  data={analytics.avgResponseTimes}
                  onEndpointClick={handleEndpointClick}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !isError && analytics && analytics.totalEvents === 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No Data Available</AlertTitle>
          <AlertDescription>
            There are no audit log events for the selected time range. Try selecting a different
            time period.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
